import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Button,
  Divider,
  Alert,
  CircularProgress,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  History as HistoryIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Refresh as RefreshIcon,
  FolderOpen as FolderIcon,
  Rule as RuleIcon,
  Preview as PreviewIcon,
  Delete as DeleteIcon,
  HelpOutline as HelpOutlineIcon,
} from '@mui/icons-material';
import { RuleBuilder } from '../../components/rules/RuleBuilder';
import { VersionHistoryDialog } from '../../components/rules/VersionHistoryDialog';
import { LeadPreviewDialog } from '../../components/rules/LeadPreviewDialog';
import UnbekannteLeadsDialog from '../../components/rules/UnbekannteLeadsDialog';
import DisplayConfigEditor from '../../components/rules/DisplayConfigEditor';
import {
  RuleType,
  CustomerRules,
  Rule,
  SimpleRule,
  RuleColumn,
  PreviewResult,
  PreviewTimeframe,
  LeadStatus,
  customerNames,
  timeframeLabels,
  leadStatusLabels,
} from '../../types/leadSourceRules';
import { fetchData, postData } from '../../helpers/apiUtils';

const LeadSourceRulesPage: React.FC = () => {
  // View mode: "rules" for rule editor, "display" for display config
  const [viewMode, setViewMode] = useState<'rules' | 'display'>('rules');

  // State
  const [ruleType, setRuleType] = useState<RuleType>('lps');
  const [selectedCustomer, setSelectedCustomer] = useState<string>('ft');
  const [selectedSource, setSelectedSource] = useState<string>('global');
  const [rules, setRules] = useState<CustomerRules | null>(null);
  const [originalRules, setOriginalRules] = useState<CustomerRules | null>(null);
  const [columns, setColumns] = useState<RuleColumn[]>([]);
  const [customers, setCustomers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saveComment, setSaveComment] = useState('');

  // Preview state
  const [previewTimeframe, setPreviewTimeframe] = useState<PreviewTimeframe>('today');
  const [previewLeadStatus, setPreviewLeadStatus] = useState<LeadStatus>('new');
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  // Dialog state
  const [versionHistoryOpen, setVersionHistoryOpen] = useState(false);
  const [leadPreviewOpen, setLeadPreviewOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [addSourceDialogOpen, setAddSourceDialogOpen] = useState(false);
  const [newSourceName, setNewSourceName] = useState('');
  const [unbekannteDialogOpen, setUnbekannteDialogOpen] = useState(false);
  const [unbekannteLeadPreviewOpen, setUnbekannteLeadPreviewOpen] = useState(false);
  const [unbekannteTimeframe, setUnbekannteTimeframe] = useState<PreviewTimeframe>('this_week');
  const [unbekannteLeadStatus, setUnbekannteLeadStatus] = useState<LeadStatus>('all');

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success',
  });

  // Load rules and columns
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rulesResponse, columnsResponse] = await Promise.all([
        fetchData(`/api/lead-source-rules/active/${ruleType}`),
        fetchData(`/api/lead-source-rules/columns/${ruleType}`),
      ]);

      if (rulesResponse.success && rulesResponse.data) {
        const rulesData = rulesResponse.data as {
          rules: CustomerRules;
          customers: string[];
        };
        setRules(rulesData.rules);
        setOriginalRules(JSON.parse(JSON.stringify(rulesData.rules)));
        setCustomers(rulesData.customers || []);
        if (!rulesData.customers?.includes(selectedCustomer)) {
          setSelectedCustomer(rulesData.customers?.[0] || 'ft');
        }
      } else {
        setError(rulesResponse.error || 'Failed to load rules');
      }

      if (columnsResponse.success && columnsResponse.data) {
        setColumns(columnsResponse.data as RuleColumn[]);
      }
    } catch (err) {
      setError('Failed to load data');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  }, [ruleType, selectedCustomer]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Check for changes
  useEffect(() => {
    if (rules && originalRules) {
      setHasChanges(JSON.stringify(rules) !== JSON.stringify(originalRules));
    }
  }, [rules, originalRules]);

  // Get sources for current customer
  const getSources = (): string[] => {
    if (!rules || !rules[selectedCustomer]) return [];
    return Object.keys(rules[selectedCustomer]);
  };

  // Get current rules for selected source
  const getCurrentRules = (): Rule[] => {
    if (!rules || !rules[selectedCustomer] || !rules[selectedCustomer][selectedSource]) {
      return [];
    }
    return rules[selectedCustomer][selectedSource];
  };

  // Update rules for current source
  const updateCurrentRules = (newRules: Rule[]) => {
    if (!rules) return;
    const updated = { ...rules };
    if (!updated[selectedCustomer]) {
      updated[selectedCustomer] = {};
    }
    updated[selectedCustomer][selectedSource] = newRules;
    setRules(updated);
  };

  // Add new source
  const handleAddSource = () => {
    if (!newSourceName.trim() || !rules) return;
    const sourceName = newSourceName.trim().toLowerCase().replace(/\s+/g, '_');
    const updated = { ...rules };
    if (!updated[selectedCustomer]) {
      updated[selectedCustomer] = {};
    }
    if (updated[selectedCustomer][sourceName]) {
      setSnackbar({
        open: true,
        message: 'Source already exists',
        severity: 'error',
      });
      return;
    }
    updated[selectedCustomer][sourceName] = [];
    setRules(updated);
    setSelectedSource(sourceName);
    setAddSourceDialogOpen(false);
    setNewSourceName('');
  };

  // Delete source
  const handleDeleteSource = (source: string) => {
    if (!rules || source === 'global' || source === 'all') return;
    const updated = { ...rules };
    delete updated[selectedCustomer][source];
    setRules(updated);
    setSelectedSource('global');
  };

  // Run preview
  const runPreview = async () => {
    if (!rules) return;
    setPreviewLoading(true);
    setPreviewResult(null);
    try {
      const response = await postData('/api/lead-source-rules/preview', {
        rule_type: ruleType,
        customer: selectedCustomer,
        source: selectedSource,
        rules,
        timeframe: previewTimeframe,
        lead_status: previewLeadStatus,
      });
      if (response.success && response.data) {
        setPreviewResult(response.data as PreviewResult);
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Preview failed',
          severity: 'error',
        });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Preview failed', severity: 'error' });
    } finally {
      setPreviewLoading(false);
    }
  };

  // Save rules
  const handleSave = async () => {
    if (!rules) return;
    setSaving(true);
    try {
      const response = await postData('/api/lead-source-rules/save', {
        rule_type: ruleType,
        rules,
        comment: saveComment,
      });
      if (response.success && response.data) {
        const saveData = response.data as { version_number: number };
        setSnackbar({
          open: true,
          message: `Gespeichert als Version ${saveData.version_number}`,
          severity: 'success',
        });
        setOriginalRules(JSON.parse(JSON.stringify(rules)));
        setSaveDialogOpen(false);
        setSaveComment('');
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Save failed',
          severity: 'error',
        });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Save failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Discard changes
  const handleDiscard = () => {
    if (originalRules) {
      setRules(JSON.parse(JSON.stringify(originalRules)));
    }
  };

  // Handle version revert
  const handleVersionRevert = async (versionId: number) => {
    try {
      const response = await postData(`/api/lead-source-rules/revert/${versionId}`, {});
      if (response.success) {
        setSnackbar({
          open: true,
          message: `Zurückgesetzt zu Version ${versionId}`,
          severity: 'success',
        });
        setVersionHistoryOpen(false);
        loadData();
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Revert failed',
          severity: 'error',
        });
      }
    } catch (err) {
      setSnackbar({ open: true, message: 'Revert failed', severity: 'error' });
    }
  };

  // Create rule from Unbekannte Leads distribution
  const handleCreateRuleFromUnbekannte = (
    column: string,
    value: string | null,
    targetSource: string,
  ) => {
    if (!rules) return;
    const updated = { ...rules };
    if (!updated[selectedCustomer]) {
      updated[selectedCustomer] = {};
    }
    if (!updated[selectedCustomer][targetSource]) {
      updated[selectedCustomer][targetSource] = [];
    }

    const newRule: SimpleRule =
      value === null
        ? { type: 'simple', column, operator: 'IS NULL', value: null }
        : { type: 'simple', column, operator: '=', value };

    updated[selectedCustomer][targetSource] = [...updated[selectedCustomer][targetSource], newRule];
    setRules(updated);
    setSelectedSource(targetSource);
    setUnbekannteDialogOpen(false);
    setSnackbar({
      open: true,
      message: `Regel hinzugefügt: ${column} ${value === null ? 'IS NULL' : `= "${value}"`} → ${targetSource}`,
      severity: 'success',
    });
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '60vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
        <Button onClick={loadData} sx={{ mt: 2 }}>
          Erneut versuchen
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
        }}
      >
        <Typography variant="h4">Lead Source Rules Editor</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<HelpOutlineIcon />}
            onClick={() => setUnbekannteDialogOpen(true)}
          >
            Unbekannte Leads
          </Button>
          <Button
            variant="outlined"
            startIcon={<HistoryIcon />}
            onClick={() => setVersionHistoryOpen(true)}
          >
            Version History
          </Button>
        </Box>
      </Box>

      {/* Controls */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Customer</InputLabel>
          <Select
            value={selectedCustomer}
            label="Customer"
            onChange={(e) => {
              setSelectedCustomer(e.target.value);
              setSelectedSource('global');
            }}
          >
            {customers.map((c) => (
              <MenuItem key={c} value={c}>
                {customerNames[c] || c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {viewMode === 'rules' && (
          <ToggleButtonGroup
            value={ruleType}
            exclusive
            onChange={(_, newType) => {
              if (newType) {
                setRuleType(newType);
                setSelectedSource('global');
              }
            }}
          >
            <ToggleButton value="lps">LPS</ToggleButton>
            <ToggleButton value="vnr">VNR</ToggleButton>
          </ToggleButtonGroup>
        )}

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(_, newMode) => {
            if (newMode) setViewMode(newMode);
          }}
          sx={{ ml: 2 }}
        >
          <ToggleButton value="rules">Regeln</ToggleButton>
          <ToggleButton value="display">Anzeige</ToggleButton>
        </ToggleButtonGroup>

        <Box sx={{ flex: 1 }} />

        {hasChanges && (
          <Alert severity="warning" sx={{ py: 0 }}>
            Ungespeicherte Änderungen
          </Alert>
        )}
      </Box>

      {/* Display Config Editor */}
      {viewMode === 'display' && <DisplayConfigEditor selectedCustomer={selectedCustomer} />}

      {/* Main Content (Rules Editor) */}
      {viewMode === 'rules' && (
        <>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Sources Sidebar */}
            <Paper sx={{ width: 250, p: 0 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Sources
                </Typography>
              </Box>
              <List dense sx={{ maxHeight: 500, overflow: 'auto' }}>
                {getSources().map((source) => (
                  <ListItem
                    key={source}
                    disablePadding
                    secondaryAction={
                      source !== 'global' &&
                      source !== 'all' && (
                        <Tooltip title="Delete Source">
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteSource(source);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )
                    }
                  >
                    <ListItemButton
                      selected={selectedSource === source}
                      onClick={() => setSelectedSource(source)}
                    >
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {source === 'global' ? (
                          <FolderIcon fontSize="small" />
                        ) : (
                          <RuleIcon fontSize="small" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={source}
                        primaryTypographyProps={{
                          fontWeight: source === 'global' ? 'bold' : 'normal',
                          fontStyle: source === 'global' ? 'italic' : 'normal',
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              <Box sx={{ p: 1, borderTop: '1px solid #e0e0e0' }}>
                <Button
                  fullWidth
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setAddSourceDialogOpen(true)}
                >
                  Add Source
                </Button>
              </Box>
            </Paper>

            {/* Rule Builder */}
            <Paper sx={{ flex: 1, p: 2 }}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                }}
              >
                <Typography variant="h6">
                  Rules for <strong>{selectedSource}</strong>
                  {selectedSource === 'global' && (
                    <Typography
                      component="span"
                      variant="body2"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      (Applied to all sources)
                    </Typography>
                  )}
                </Typography>
              </Box>

              <RuleBuilder
                rules={getCurrentRules()}
                columns={columns}
                onChange={updateCurrentRules}
              />
            </Paper>
          </Box>

          {/* Preview Section */}
          <Paper sx={{ mt: 2, p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                Preview
              </Typography>

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Zeitraum</InputLabel>
                <Select
                  value={previewTimeframe}
                  label="Zeitraum"
                  onChange={(e) => setPreviewTimeframe(e.target.value as PreviewTimeframe)}
                >
                  {(Object.keys(timeframeLabels) as PreviewTimeframe[]).map((tf) => (
                    <MenuItem key={tf} value={tf}>
                      {timeframeLabels[tf]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Lead-Status</InputLabel>
                <Select
                  value={previewLeadStatus}
                  label="Lead-Status"
                  onChange={(e) => setPreviewLeadStatus(e.target.value as LeadStatus)}
                  disabled={ruleType === 'vnr'}
                >
                  {(Object.keys(leadStatusLabels) as LeadStatus[]).map((ls) => (
                    <MenuItem key={ls} value={ls}>
                      {leadStatusLabels[ls]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                size="small"
                startIcon={previewLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
                onClick={runPreview}
                disabled={previewLoading}
              >
                Run Preview
              </Button>

              {previewResult && (
                <>
                  <Typography>
                    Matching leads: <strong>{previewResult.count.toLocaleString()}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ({previewResult.query_time_ms}ms)
                  </Typography>
                  <Button
                    variant="text"
                    size="small"
                    startIcon={<PreviewIcon />}
                    onClick={() => setLeadPreviewOpen(true)}
                  >
                    Show Leads
                  </Button>
                </>
              )}
            </Box>
          </Paper>

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
            <Button variant="outlined" onClick={handleDiscard} disabled={!hasChanges}>
              Discard Changes
            </Button>
            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={() => setSaveDialogOpen(true)}
              disabled={!hasChanges || saving}
            >
              Save Changes
            </Button>
          </Box>
        </>
      )}

      {/* Save Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => setSaveDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Save Rules</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Comment (optional)"
            value={saveComment}
            onChange={(e) => setSaveComment(e.target.value)}
            placeholder="Describe your changes..."
            multiline
            rows={2}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSaveDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Source Dialog */}
      <Dialog
        open={addSourceDialogOpen}
        onClose={() => setAddSourceDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add New Source</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Source Name"
            value={newSourceName}
            onChange={(e) => setNewSourceName(e.target.value)}
            placeholder="e.g. newsletter, traffic"
            sx={{ mt: 2 }}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddSourceDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSource} disabled={!newSourceName.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version History Dialog */}
      <VersionHistoryDialog
        open={versionHistoryOpen}
        onClose={() => setVersionHistoryOpen(false)}
        ruleType={ruleType}
        onRevert={handleVersionRevert}
      />

      {/* Lead Preview Dialog */}
      <LeadPreviewDialog
        open={leadPreviewOpen}
        onClose={() => setLeadPreviewOpen(false)}
        ruleType={ruleType}
        customer={selectedCustomer}
        source={selectedSource}
        rules={rules}
        timeframe={previewTimeframe}
        leadStatus={previewLeadStatus}
      />

      {/* Unbekannte Leads Dialog */}
      <UnbekannteLeadsDialog
        open={unbekannteDialogOpen}
        onClose={() => setUnbekannteDialogOpen(false)}
        ruleType={ruleType}
        customer={selectedCustomer}
        rules={rules}
        existingSources={getSources()}
        onCreateRule={handleCreateRuleFromUnbekannte}
        onShowLeadPreview={(tf, ls) => {
          setUnbekannteTimeframe(tf);
          setUnbekannteLeadStatus(ls);
          setUnbekannteLeadPreviewOpen(true);
        }}
      />

      {/* Lead Preview Dialog for "other" (unbekannte) leads */}
      <LeadPreviewDialog
        open={unbekannteLeadPreviewOpen}
        onClose={() => setUnbekannteLeadPreviewOpen(false)}
        ruleType={ruleType}
        customer={selectedCustomer}
        source="other"
        rules={rules}
        timeframe={unbekannteTimeframe}
        leadStatus={unbekannteLeadStatus}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeadSourceRulesPage;
